<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarDays, ClipboardList, CreditCard, Ticket, Check, ChevronRight } from '@lucide/vue'
import { Dialog, DialogTrigger, DialogScrollContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'

type EventTemplate = { key: string; title: string; description: string; icon: string }
const props = defineProps<{ templates: EventTemplate[]; initialValue?: string }>()
const { t } = useI18n()
const selected = ref(props.initialValue || '')
const open = ref(false)
const current = computed(() => props.templates.find(template => template.key === selected.value))
const icons = { CalendarDays, ClipboardList, CreditCard, Ticket }
function select(key: string): void {
  selected.value = key
  open.value = false
}
</script>

<template>
  <section aria-labelledby="event-template-title">
    <input type="hidden" name="template" :value="selected">
    <h2 id="event-template-title" class="tw:m-0 tw:mb-4 tw:text-base tw:font-semibold tw:text-balance">{{ t('Event template') }}</h2>
    <Dialog v-model:open="open">
      <DialogTrigger type="button" class="tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-4 tw:text-left tw:transition-colors tw:duration-150 tw:hover:bg-secondary tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-ring tw:motion-reduce:transition-none">
        <component :is="icons[current?.icon as keyof typeof icons] || ClipboardList" class="tw:size-6 tw:shrink-0" :stroke-width="1.5" aria-hidden="true" />
        <span class="tw:min-w-0 tw:flex-1">
          <span class="tw:block tw:font-medium tw:text-sm">{{ current?.title || t('Choose an event template') }}</span>
          <span class="tw:mt-1 tw:block tw:text-sm tw:leading-relaxed tw:text-pretty tw:text-muted-foreground">{{ current?.description || t('Prepare settings, draft emails and configuration checks for your event.') }}</span>
        </span>
        <ChevronRight class="tw:size-4 tw:shrink-0 tw:text-muted-foreground" :stroke-width="1.5" aria-hidden="true" />
      </DialogTrigger>
      <DialogScrollContent size="lg">
        <DialogHeader>
          <DialogTitle>{{ t('Choose an event template') }}</DialogTitle>
          <DialogDescription>{{ t('Start with a template. You can adjust your event configuration afterwards.') }}</DialogDescription>
        </DialogHeader>
        <div class="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
          <button v-for="template in templates" :key="template.key" type="button"
            :aria-pressed="selected === template.key"
            class="tw:relative tw:flex tw:flex-col tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-4 tw:text-left tw:hover:bg-secondary tw:focus-visible:outline-2 tw:focus-visible:outline-ring tw:focus-visible:outline-offset-2 tw:transition-colors tw:duration-150 tw:motion-reduce:transition-none"
            :class="{ 'tw:ring-2 tw:ring-primary tw:border-primary': selected === template.key }"
            @click="select(template.key)">
            <component :is="icons[template.icon as keyof typeof icons]" class="tw:size-7 tw:text-primary" aria-hidden="true" />
            <Check v-if="selected === template.key" class="tw:absolute tw:right-3 tw:top-3 tw:size-4" aria-hidden="true" />
            <span class="tw:pr-5 tw:font-semibold tw:text-balance">{{ template.title }}</span>
            <span class="tw:text-sm tw:leading-relaxed tw:text-pretty tw:text-muted-foreground">{{ template.description }}</span>
          </button>
        </div>
        <button type="button" class="tw:min-h-11 tw:rounded-md tw:px-3 tw:text-sm tw:text-muted-foreground tw:underline tw:underline-offset-4 tw:hover:text-foreground tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-ring" @click="select('')">{{ t('Start without a template') }}</button>
      </DialogScrollContent>
    </Dialog>
  </section>
</template>
